/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/forbid-prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { useMutation, useQueryClient } from 'react-query';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ModalBodyLayout from '../../../shared/ModalBodyLayout';
import { WalletScreens } from '../../constants';
import { showAlert } from '../../../../utilities/notifications';
import { sharedSetting } from '../../../../utilities/constants';
import { PHONE_VERIFICATION_SEND_AUTHY } from '../../../../utilities/sharedMutations';
import { useUserPersonalInfo } from '../../../../utilities/sharedHooks/user';
import { BANK_WITHDRAW } from '../../mutations';
import ConfirmPhoneNumber from '../../../../components/ConfirmPhone';
import { WALLETS_ALL } from '../../queries';
import { RECAPTCHA_EVENTS } from '../../../Onboarding/constants';
import { useSetting } from '../../../../utilities/sharedHooks/settings';
import { handleFormBlur } from '../../../../utilities/formsUtils';
import RecaptchaModal from '../../../shared/RecaptchaModal';
import { cacheRecaptchaV3failure, clearRecaptchaV3failure } from '../../../../utilities/caching';

const FiatWalletWithdrawVerify = ({ options, onCloseModal, openScreen }) => {
  const { wallet, fiatAmount } = options;
  const { t: translate } = useTranslation();
  const [timer, setTimer] = useState(sharedSetting.RESEND_WAIT_TIME);
  const {
    countryCode,
    phoneNumber,
    id: userId,
    authyId,
    totpEnabled,
    totpActive,
  } = useUserPersonalInfo();
  const [form, setForm] = useState({
    code: '',
    confirmed_phone_number: `${countryCode} ${phoneNumber}`,
  });
  const queryClient = useQueryClient();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [isOpenRecV2, setIsOpenRecV2] = useState(false);

  const [recV2Token, setRecV2Token] = useState('');

  const [totpFeatureEnabled] = useSetting('app_features', 'feature_allow_totp');

  const isTOTP = totpEnabled && totpFeatureEnabled && totpActive;

  const interval = useRef(
    setTimeout(() => {
      if (timer > 0) setTimer(timer - 1);
    }, 1000)
  );

  const { mutate: requestBankWithdrawal, isLoading } = useMutation(BANK_WITHDRAW, {
    onSettled: (_, error) => {
      if (error) {
        showAlert({ message: error.message, type: 'danger' });
      } else {
        clearTimeout(interval.current);
        onCloseModal();
        showAlert({
          message:
            translate('fiat_withdrawal_success_label') + fiatAmount + wallet.coin.toUpperCase(),
          type: 'success',
        });
        queryClient.resetQueries(WALLETS_ALL);
      }
    },
  });

  const { mutate: requestPhoneVerification } = useMutation(PHONE_VERIFICATION_SEND_AUTHY, {
    onSettled: (data, error) => {
      if (error) {
        if (error?.response?.status === 307) {
          setIsOpenRecV2(true);
          cacheRecaptchaV3failure();
        } else {
          setIsOpenRecV2(false);
          clearRecaptchaV3failure();
          showAlert({ message: error.message, type: 'danger' });
        }
      } else {
        setIsOpenRecV2(false);
        clearRecaptchaV3failure();
        if (data.status >= 400) {
          showAlert({ message: data.message, type: 'danger' });
        } else {
          showAlert({ message: data.message, type: 'success' });
        }
        setTimer(sharedSetting.RESEND_WAIT_TIME);
      }
    },
  });

  const handleRequestPhoneNumberVerification = async () => {
    try {
      const token = await executeRecaptcha(RECAPTCHA_EVENTS.VERIFY_PHONE_NUMBER);
      if (!token) {
        return false;
      }

      requestPhoneVerification({ authy_id: authyId, user_id: userId, mode: 'sms', token });
    } catch (err) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!isTOTP) {
      handleRequestPhoneNumberVerification();
    }
  }, []);

  const onSubmit = (e) => {
    e.preventDefault();
    requestBankWithdrawal({ ...options, otpauth: form.code });
    clearTimeout(interval);
  };

  const renderFooter = () => (
    <>
      <button
        style={{ marginTop: '100px' }}
        className="btn btn-primary w-100 py-3"
        type="submit"
        loading={isLoading ? 'true' : 'false'}
        disabled={form.code === '' || form.code === null || form.code === undefined}
        form="confirm-phone-form">
        {translate('confirm_button_label')}
      </button>
      {!isTOTP && (
        <button
          type="button"
          onClick={() => handleRequestPhoneNumberVerification()}
          disabled={timer > 0}
          className={`btn ${timer > 0 ? 'btn-disabled' : 'btn-outline-primary'} w-100 py-3 mt-4`}>
          {translate('resend_button_label')}
        </button>
      )}
    </>
  );

  const handleChange = (e) => {
    if (!e || !e.target) return;
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const clearError = (e) => {
    e.target.className = e.target.className.replace('has-error', '');
  };

  const onChangeRecV2 = async (tokenV2) => {
    setRecV2Token(tokenV2);
    requestPhoneVerification({ authy_id: authyId, user_id: userId, mode: 'sms', token: tokenV2 });
  };

  return (
    <>
      <ModalBodyLayout
        withBack
        onPrevClick={() => openScreen(WalletScreens.FIAT_WITHDRAWAL, { ...options })}
        title={translate('fiat_withdrawal_verify_title')}
        renderFooter={renderFooter}>
        {!isTOTP && (
          <ConfirmPhoneNumber
            user={{ country_code: countryCode }}
            isPhone={false}
            onSubmit={onSubmit}
            timer={timer}
            form={form}
            setForm={setForm}
          />
        )}
        {isTOTP && (
          <form onSubmit={onSubmit} id="confirm-phone-form">
            <p className="text-f5 text-dark mt-5">{translate('totp_enter_code_title')}</p>
            <div className="form-group">
              <input
                type="number"
                className="form-control-input"
                placeholder={translate('totp_code_input_placeholder')}
                onChange={handleChange}
                value={form.code}
                onBlur={handleFormBlur}
                onFocus={clearError}
                name="code"
                autoComplete="off"
                required
              />
              <label className="floating-label">{translate('totp_code_input_placeholder')}</label>
              <label className="error-label">{translate('verification_code_error_label')}</label>
              <label className="text-f6 w-95 mt-2 text-shadow">
                {translate('totp_code_input_note')}
              </label>
            </div>
          </form>
        )}
      </ModalBodyLayout>
      <RecaptchaModal
        onChange={onChangeRecV2}
        isLoading={isLoading}
        resetToken={() => setRecV2Token('')}
        token={recV2Token}
        setIsModalVisible={setIsOpenRecV2}
        isModalVisible={isOpenRecV2}
      />
    </>
  );
};

export default FiatWalletWithdrawVerify;
FiatWalletWithdrawVerify.propTypes = {
  options: PropTypes.object,
  openScreen: PropTypes.func.isRequired,
  onCloseModal: PropTypes.func.isRequired,
};

FiatWalletWithdrawVerify.defaultProps = {
  options: null,
};
